import { Store } from 'antd/lib/form/interface';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';
import {
  OptionProps,
  ReportKey,
  ReportsFilters,
  ReportsGroupBy,
  ReportsNamedCount,
  ReportsReportOptions,
  ReportsSortOrder,
} from '@models/interfaces';
import type { StructuralPassFailGroupBy } from './structural-pass-fail.constants';
import { STRUCTURAL_PASS_FAIL_GROUP_BY_VALUES } from './structural-pass-fail.constants';

export const getOptionString = (value: OptionProps['viewValue']): string =>
  typeof value === 'string' ? value : '';

export const isArtificialAllOption = ({
  value,
  viewValue,
}: OptionProps): boolean =>
  getOptionString(viewValue).toLowerCase() === 'all' ||
  value === '' ||
  String(value).toLowerCase() === 'all';

export const getProjectTypeOptions = (options: OptionProps[]): OptionProps[] =>
  options.filter((option: OptionProps) => !isArtificialAllOption(option));

export const getProjectTypeFilterValues = (
  values: ReportsFilters['projectType'],
  options: OptionProps[],
): ReportsFilters['projectType'] => {
  if (!Array.isArray(values) || !values.length) {
    return undefined;
  }

  const artificialValues = new Set(
    options
      .filter((option: OptionProps) => isArtificialAllOption(option))
      .map(({ value }: OptionProps) => value),
  );
  const nextValues = values.filter(
    (value: string) => !artificialValues.has(value),
  );

  return nextValues.length ? nextValues : undefined;
};

export const isStructuralPassFailReport = (reportKey: ReportKey): boolean =>
  reportKey === ReportKey.StructuralPassFail;

export const isProjectDurationReport = (reportKey: ReportKey): boolean =>
  reportKey === ReportKey.ProjectDuration;

export const isNewClosedProjectsReport = (reportKey: ReportKey): boolean =>
  reportKey === ReportKey.NewClosedProjects;

export const isPaceTaskFilterSupported = (reportKey: ReportKey): boolean =>
  reportKey === ReportKey.OverdueTasks ||
  reportKey === ReportKey.DatesAtAGlance;

export const isPermittingReport = (reportKey: ReportKey): boolean =>
  reportKey === ReportKey.PermittingOverview ||
  reportKey === ReportKey.BPExpiration;

export const isProjectNumberFilterSupported = isPaceTaskFilterSupported;

const REPORT_DATE_RANGE_FIELDS: Partial<
  Record<ReportKey, [keyof ReportsFilters, keyof ReportsFilters]>
> = {
  [ReportKey.PermittingOverview]: ['dateFrom', 'dateTo'],
  [ReportKey.DatesAtAGlance]: ['milestoneDateFrom', 'milestoneDateTo'],
  [ReportKey.NewClosedProjects]: ['periodFrom', 'periodTo'],
  [ReportKey.OverdueTasks]: ['forecastedDateFrom', 'forecastedDateTo'],
  [ReportKey.ProjectDuration]: ['actualDateFrom', 'actualDateTo'],
};

export const getReportDateRangeFields = (
  reportKey: ReportKey,
): [keyof ReportsFilters, keyof ReportsFilters] | undefined =>
  REPORT_DATE_RANGE_FIELDS[reportKey];

export const getReportGroupByOptions = (
  reportKey: ReportKey,
  options: OptionProps[],
): OptionProps[] =>
  isStructuralPassFailReport(reportKey)
    ? options.filter(({ value }: OptionProps) =>
        STRUCTURAL_PASS_FAIL_GROUP_BY_VALUES.includes(
          value as StructuralPassFailGroupBy,
        ),
      )
    : options;

export const getMetricLabel = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (value: string) => value.toUpperCase());

export const getTableSortOrder = (
  sortOrder?: ReportsSortOrder,
): SortOrder | undefined => {
  if (sortOrder === ReportsSortOrder.ASC) {
    return 'ascend';
  }

  if (sortOrder === ReportsSortOrder.DESC) {
    return 'descend';
  }

  return undefined;
};

export const getReportsSortOrder = (
  sortOrder?: SortOrder,
): ReportsSortOrder | undefined => {
  if (sortOrder === 'ascend') {
    return ReportsSortOrder.ASC;
  }

  if (sortOrder === 'descend') {
    return ReportsSortOrder.DESC;
  }

  return undefined;
};

export const isOptionValueAllowed = (
  options: OptionProps[],
  value?: string,
): boolean =>
  !value || options.some((option: OptionProps) => option.value === value);

const isReportGroupByAllowed = (
  reportKey: ReportKey,
  options: OptionProps[],
  value?: ReportsGroupBy,
): boolean =>
  !value ||
  (isStructuralPassFailReport(reportKey)
    ? STRUCTURAL_PASS_FAIL_GROUP_BY_VALUES.includes(
        value as StructuralPassFailGroupBy,
      )
    : isOptionValueAllowed(options, value));

export const sanitizeReportsFiltersForReport = (
  reportKey: ReportKey,
  filters: ReportsFilters,
  reportOptions?: Partial<Record<ReportKey, ReportsReportOptions>>,
): ReportsFilters => {
  const selectedReportOptions = reportOptions?.[reportKey];
  const groupByOptions = selectedReportOptions
    ? getReportGroupByOptions(reportKey, selectedReportOptions.groupByOptions)
    : [];
  const sortByOptions = selectedReportOptions?.sortByOptions ?? [];
  const isSortByAllowed =
    !filters.sortBy ||
    !selectedReportOptions ||
    isOptionValueAllowed(sortByOptions, filters.sortBy);

  const isPaceTaskAllowed = isPaceTaskFilterSupported(reportKey);
  const isProjectNumberAllowed = isProjectNumberFilterSupported(reportKey);
  const dateRangeFields = getReportDateRangeFields(reportKey);

  const baseFilters: ReportsFilters = {
    groupBy:
      !isPermittingReport(reportKey) &&
      isReportGroupByAllowed(reportKey, groupByOptions, filters.groupBy)
        ? filters.groupBy
        : undefined,
    metric: isProjectDurationReport(reportKey) ? filters.metric : undefined,
    page: filters.page,
    paceTask: isPaceTaskAllowed ? filters.paceTask : undefined,
    paceTaskScreen: isPaceTaskAllowed ? filters.paceTaskScreen : undefined,
    progressStatus: filters.progressStatus,
    projectNumber: isProjectNumberAllowed ? filters.projectNumber : undefined,
    projectStatus: filters.projectStatus,
    projectSubtype: filters.projectSubtype,
    projectType: filters.projectType,
    providenceAgent: filters.providenceAgent,
    permittingAgent: isPermittingReport(reportKey)
      ? filters.permittingAgent
      : undefined,
    dateField:
      reportKey === ReportKey.PermittingOverview
        ? filters.dateField
        : undefined,
    size: filters.size,
    sortBy: isSortByAllowed ? filters.sortBy : undefined,
    sortOrder: isSortByAllowed ? filters.sortOrder : undefined,
    state: filters.state,
    submarket: filters.submarket,
    towerOwner: filters.towerOwner,
  };

  if (!dateRangeFields) {
    return baseFilters;
  }

  const [dateFromField, dateToField] = dateRangeFields;

  return {
    ...baseFilters,
    [dateFromField]: filters[dateFromField],
    [dateToField]: filters[dateToField],
  };
};

export const getReportFiltersForReportChange = (
  reportKey: ReportKey,
  filters: ReportsFilters,
  reportOptions?: Partial<Record<ReportKey, ReportsReportOptions>>,
): ReportsFilters => ({
  ...sanitizeReportsFiltersForReport(reportKey, filters, reportOptions),
  page: 1,
});

export const getTableSorter = (
  sorter: SorterResult<Store> | SorterResult<Store>[],
): SorterResult<Store> =>
  Array.isArray(sorter)
    ? sorter.find(({ order }: SorterResult<Store>) => order) ?? sorter[0] ?? {}
    : sorter;

export const getAgentChartData = (
  overdueByAgent: ReportsNamedCount[] = [],
): ReportsNamedCount[] =>
  overdueByAgent.map((agent: ReportsNamedCount) => ({
    ...agent,
    name: agent.name || 'Unassigned',
  }));
